import { Heart, MapPin, Phone, Mail, Clock } from "lucide-react";
import { clinic, branches } from "../data";

export default function Footer({ selectedBranch }) {
  const currentBranch = branches[selectedBranch];
  const branchNames = Object.values(branches).map(b => b.shortName).join(", ");

  const safePhone = currentBranch?.contact?.phoneDisplay || "";
  const safeEmail = currentBranch?.contact?.email || clinic?.email || "";
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-black text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Brand Column - FIXED: Logo instead of "O" */}
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
              <img
                src="/images/olive11.png"
                alt="Olive Dental Clinic Logo"
                width="60"
                height="60"
                loading="lazy"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="text-xl font-bold">{clinic?.name || "Olive Dental Clinic"}</h3>
                <p className="text-sm text-gray-400">Dental Clinic</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Providing exceptional dental care with compassion and expertise across {branchNames}.
            </p>
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><a href="/" className="text-gray-400 hover:text-[#4a7c59] transition-colors">Home</a></li>
              <li><a href="#about" className="text-gray-400 hover:text-[#4a7c59] transition-colors">About Us</a></li>
              <li><a href="#doctors" className="text-gray-400 hover:text-[#4a7c59] transition-colors">Our Doctors</a></li>
              <li><a href="#services" className="text-gray-400 hover:text-[#4a7c59] transition-colors">Services</a></li>
              <li><a href="#gallery" className="text-gray-400 hover:text-[#4a7c59] transition-colors">Gallery</a></li>
              <li><a href="#faq" className="text-gray-400 hover:text-[#4a7c59] transition-colors">FAQ</a></li>
              <li><a href="#contact" className="text-gray-400 hover:text-[#4a7c59] transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact Info - FIXED: All clickable */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold text-lg mb-4">Contact Info</h4>
            <ul className="space-y-3">
              <li className="flex items-center justify-center md:justify-start gap-3">
                <MapPin className="w-5 h-5 text-[#4a7c59] flex-shrink-0" />
                {/* FIXED: Clickable address with Google Maps */}
                <a
                  href={currentBranch?.mapLink || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 text-sm hover:text-[#4a7c59] transition-colors"
                >
                  {currentBranch?.fullAddress || "Address available"}
                </a>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Phone className="w-5 h-5 text-[#4a7c59] flex-shrink-0" />
                {/* FIXED: Clickable phone */}
                <a
                  href={`tel:${currentBranch?.contact?.phone || ""}`}
                  className="text-gray-400 hover:text-[#4a7c59] transition-colors"
                >
                  {safePhone}
                </a>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Mail className="w-5 h-5 text-[#4a7c59] flex-shrink-0" />
                {/* FIXED: Clickable email */}
                <a
                  href={`mailto:${safeEmail}`}
                  className="text-gray-400 hover:text-[#4a7c59] transition-colors"
                >
                  {safeEmail}
                </a>
              </li>
              <li className="flex items-center justify-center md:justify-start gap-3">
                <Clock className="w-5 h-5 text-[#4a7c59] flex-shrink-0" />
                <div className="text-gray-400 text-sm">
                  <p>{currentBranch?.workingHours?.display || "Mon-Sat: 9AM-7PM"}</p>
                  <p>Sunday: {currentBranch?.workingHours?.holiday || "Closed"}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* NEW: Services Line - SEO */}
        <div className="text-center mb-6">
          <p className="text-gray-500 text-xs leading-6">
            Dental Implants • Root Canal Treatment • Aligners • 
            Teeth Cleaning • Cosmetic Dentistry • Kids Dentistry
          </p>
        </div>

        {/* NEW: Area Served - Local SEO */}
        <div className="text-center mb-6">
          <p className="text-gray-500 text-xs">
            Serving Mangalam, Koottayi, Vettom, Tirur and Malappuram.
          </p>
        </div>

        {/* Copyright and Credit - FIXED: Dynamic year */}
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-400 text-xs md:text-sm">
            © {currentYear} {clinic?.name || "Olive Dental Clinic"}. All rights reserved. | Designed with <Heart className="w-4 h-4 text-red-500 inline" /> for healthy smiles
          </p>
          <p className="mt-2 text-xs text-gray-400">
            Website Designed &amp; Developed by{" "}
            <a
              href="https://rashnex.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold tracking-wide text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              RASHNEX Digital Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}